import { DOCUMENT, Injectable, inject, signal } from '@angular/core';
import { BrowserPlatformService } from '../browser-platform/browser-platform.service';

export type SiteTheme = 'light' | 'dark' | 'system';

/** localStorage key - the no-flash scripts in src/index.html and _AngularCssEsi.cshtml read the same key */
export const SITE_THEME_STORAGE_KEY = 'siteTheme';
const DARK_CLASS = 'dark-mode';
/** browser-chrome color while dark (= --surface in styles/basics/_css-variables.scss) */
const DARK_THEME_COLOR = '#1c1f22';
/**
 * Nothing stored = light while the color sweep runs: the public never sees dark unless they
 * chose it, staff are seeded 'dark' once (seedStaffDefault) and only a STORED 'system' follows
 * the os preference. At release this becomes 'system'. MUST match the inline scripts in
 * src/index.html, Views/Shared/_AngularCssEsi.cshtml and Views/Users/LoginFrame.cshtml.
 */
const DEFAULT_PREFERENCE: SiteTheme = 'light';

/**
 * The site color theme (public pages). The theme lives in css only: `html.dark-mode` swaps the
 * tokens in styles/basics/_css-variables.scss and markup never depends on it, so the ssr output
 * is the same in both themes. An inline script sets the class before first paint; this service
 * owns it afterwards (the menu control, os changes) and persists the choice per browser.
 * Injected by AppComponent at bootstrap (~1.5 kB of the initial budget, paid for by RavService
 * leaving the eager graph on 2026-09-17).
 */
@Injectable({
    providedIn: 'root',
})
export class ThemeService {
    private readonly _browser = inject(BrowserPlatformService);
    private readonly _document = inject(DOCUMENT);
    private _systemDark: MediaQueryList | null = null;
    private _lightThemeColor: string | null = null;

    /** the stored choice; 'system' = follow prefers-color-scheme */
    public readonly preference = signal<SiteTheme>(DEFAULT_PREFERENCE);
    /** whether the dark class is on <html> right now */
    public readonly isDark = signal(false);

    constructor() {
        if (!this._browser.isBrowser) {
            return;
        }
        this.preference.set(this._load());
        const win = this._browser.nativeWindow;
        if (win?.matchMedia) {
            this._systemDark = win.matchMedia('(prefers-color-scheme: dark)');
            this._systemDark.addEventListener?.('change', () => this.apply());
        }
        this.apply();
    }

    public set(theme: SiteTheme): void {
        this.preference.set(theme);
        this._save(theme);
        this.apply();
    }

    /**
     * Staff test phase: a user with cms access who never chose gets 'dark' stored once (the
     * inline script picks it up from the next load on); the switch stays the way back.
     */
    public seedStaffDefault(): void {
        if (!this._browser.isBrowser || this._hasStoredChoice()) {
            return;
        }
        this.set('dark');
    }

    /** Re-applies the class from the preference; idempotent, safe after html/body class rewrites. */
    public apply(): void {
        if (!this._browser.isBrowser) {
            return;
        }
        const dark = this._resolveDark();
        this.isDark.set(dark);
        this._document.documentElement.classList.toggle(DARK_CLASS, dark);
        this._syncThemeColorMeta(dark);
    }

    private _resolveDark(): boolean {
        const preference = this.preference();
        if (preference === 'dark') {
            return true;
        }
        if (preference === 'light') {
            return false;
        }
        return !!this._systemDark?.matches;
    }

    private _load(): SiteTheme {
        const raw = this._readStored();
        return raw === 'dark' || raw === 'light' || raw === 'system' ? raw : DEFAULT_PREFERENCE;
    }

    private _hasStoredChoice(): boolean {
        const raw = this._readStored();
        return raw === 'dark' || raw === 'light' || raw === 'system';
    }

    private _readStored(): string | null {
        try {
            return this._browser.localStorage?.getItem(SITE_THEME_STORAGE_KEY) ?? null;
        } catch {
            return null;
        }
    }

    private _save(theme: SiteTheme): void {
        try {
            //'system' is stored explicitly so an explicit choice differs from "never chose"
            this._browser.localStorage?.setItem(SITE_THEME_STORAGE_KEY, theme);
        } catch {
            //storage may be blocked (private mode) - the choice just won't survive a reload
        }
    }

    /** the .net layouts and index.html carry a light theme-color meta; keep the browser chrome in sync */
    private _syncThemeColorMeta(dark: boolean): void {
        const meta = this._document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
        if (!meta) {
            return;
        }
        if (this._lightThemeColor === null) {
            this._lightThemeColor = meta.content;
        }
        meta.content = dark ? DARK_THEME_COLOR : this._lightThemeColor;
    }
}
